import { Link, router } from "@inertiajs/react"
import { Plus } from "lucide-react"
import { BackofficeLayout } from "@/components/layout/BackofficeLayout"
import { SearchInput } from "@/components/shared/SearchInput"
import { Pagination, PaginationMeta } from "@/components/shared/Pagination"
import { Button } from "@/components/ui/button"
import type { RegisteredAgentTypeSummary } from "@/types"
import { formatCents } from '@/lib/utils'

type Props = {
  types: RegisteredAgentTypeSummary[]
  search: string
  pagination: PaginationMeta
}

export default function RegisteredAgentTypesIndex({ types, search, pagination }: Props) {
  return (
    <BackofficeLayout title="Registered Agent Types" breadcrumbs={[{ label: "Backoffice", href: "/backoffice/dashboard" }, { label: "Registered Agent Types" }]}>
      <div className="mb-8">
        <h1 className="text-xl font-semibold tracking-tight">Registered Agent Types</h1>
        <p className="text-sm text-muted-foreground">{pagination.total} {pagination.total === 1 ? "type" : "types"}</p>
        <div className="flex items-center gap-2 mt-3">
          <Button size="sm" variant="outline" asChild className="gap-1.5 text-[13px] h-8">
            <Link href="/backoffice/registered_agent_types/new">
              <Plus className="w-3.5 h-3.5" />
              New Type
            </Link>
          </Button>
        </div>
      </div>

      <SearchInput value={search} placeholder="Search types..." />

      <div className="mt-4">
        {types.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <h3 className="text-sm font-medium text-foreground mb-1">No types configured yet</h3>
          </div>
        ) : (
          <div className="border rounded-lg">
            {types.map((t, i) => (
              <div
                key={t.id}
                className={`px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors ${i > 0 ? "border-t" : ""}`}
                onClick={() => router.visit(`/backoffice/registered_agent_types/${t.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Link href={`/backoffice/registered_agent_types/${t.id}`} className="text-sm font-medium hover:underline">{t.name}</Link>
                      <span className="text-[12px] text-muted-foreground font-mono">{t.code}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {t.jurisdiction_label || t.jurisdiction_code || "\u2014"} · {formatCents(t.gov_fee_cents)} + {formatCents(t.service_fee_cents)} · Min {t.min_duration_months} months
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4">
        <Pagination pagination={pagination} />
      </div>
    </BackofficeLayout>
  )
}
